import { Controller } from "stimulus"
import Swiper, { Navigation } from 'swiper';


export default class extends Controller {
  // Stimulus targets - 對應 HTML 中的 data-wishlist-slider-target 屬性
  static targets = ['swiper', 'swiperPrev', 'swiperNext']

  // Swiper 實例
  swiper = null

  connect() {
    // 初始化時先隱藏左側按鈕（因為從第一項開始）
    this.swiperPrevTarget.style.display = 'none';
    
    // 初始化 Swiper（Vue 渲染完成後執行）
    this.initializeSwiper();
    
    // 監聽視窗大小變化
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  initializeSwiper() {
    // 如果已存在 Swiper 實例，先銷毀避免記憶體洩漏
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }

    // === 容器寬度控制 ===
    // 獲取父元素實際寬度，確保 Swiper 不會超出父容器
    const parentWidth = this.swiperTarget.parentElement.offsetWidth;
    
    // 強制設置 Swiper 容器寬度約束
    this.swiperTarget.style.width = `${parentWidth}px`;     // 精確寬度
    this.swiperTarget.style.maxWidth = '100%';              // 最大寬度限制
    this.swiperTarget.style.overflowX = 'hidden';           // 隱藏水平溢出
    this.swiperTarget.style.overflowY = 'visible';          // 允許垂直溢出（dropdown顯示）

    // === 滑動項目數量計算 ===
    const slideWidth = 116;    // 每個願望清單項目的固定寬度（px）
    const spaceBetween = 12;   // 項目間距（px）
    
    // 計算在當前容器寬度下能完整顯示多少個項目
    // Math.floor 確保不會顯示部分項目
    const visibleSlides = Math.floor(parentWidth / (slideWidth + spaceBetween));

    // === Swiper 配置 ===
    this.swiper = new Swiper(this.swiperTarget, {
      // 載入導航模組
      modules: [Navigation],
      
      // === 基本設定 ===
      direction: 'horizontal',           // 水平滑動
      slidesPerView: visibleSlides,      // 同時顯示的項目數量（動態計算）
      slidesPerGroup: 2,                 // 每次滑動移動 2 個項目
      spaceBetween: spaceBetween,        // 項目間距
      loop: false,                       // 不循環（避免無限滑動）
      centeredSlides: false,             // 不居中對齊
      grabCursor: true,                  // 滑鼠懸停時顯示手型游標
      width: parentWidth,                // 明確指定 Swiper 寬度

      // === 導航按鈕配置 ===
      navigation: {
        nextEl: this.swiperNextTarget,   // 右箭頭按鈕
        prevEl: this.swiperPrevTarget,   // 左箭頭按鈕
      },

      // === 滑動行為控制 ===
      watchOverflow: true,               // 監控溢出狀態
      watchSlidesProgress: true,         // 監控滑動進度
      preventClicks: false,              // 允許點擊事件
      preventClicksPropagation: false,   // 允許點擊事件冒泡
      resistanceRatio: 0.85,             // 到達邊界時的阻力係數（0.85 = 15% 阻力）
      
      // === Touch事件優化 ===
      touchStartPreventDefault: false,   // 不阻止預設touch事件
      touchMoveStopPropagation: false,   // 不阻止touch事件冒泡
      simulateTouch: true,               // 啟用模擬觸控
      touchRatio: 1,                     // 觸控比例
      touchAngle: 45,                    // 只有滑動角度大於45度才觸發滑動
      threshold: 15,                     // 增加滑動閾值，避免誤觸
      longSwipesRatio: 0.5,             // 長滑動比例
      longSwipesMs: 300,                // 長滑動時間閾值

      // === 事件監聽 ===
      on: {
        // Swiper 初始化完成時執行
        init: (swiper) => {
          // 延遲執行確保 slides 完全載入
          setTimeout(() => {
            this.updateNavigationButtons();
          }, 50);
        },
        // 每次滑動變化時執行
        slideChange: (swiper) => {
          this.updateNavigationButtons();
        },
        // 到達開始位置時執行
        reachBeginning: (swiper) => {
          this.updateNavigationButtons();
        },
        // 到達結束位置時執行
        reachEnd: (swiper) => {
          this.updateNavigationButtons();
        }
      }
    });
  }


  //  根據當前滑動位置和溢出狀態智能顯示/隱藏左右箭頭
  updateNavigationButtons() {
    if (!this.swiper) {
      console.log('No swiper instance, returning');
      return;
    }
    
    // === 溢出檢測 ===
    // 計算是否需要滑動功能（總項目數 > 可見項目數）
    const totalSlides = this.swiper.slides?.length || 0;
    const visibleSlides = this.swiper.params?.slidesPerView || 0;
    const needsNavigation = totalSlides > visibleSlides;

    // === 左側按鈕控制 ===
    // 只有在需要導航且不在開始位置時才顯示左箭頭
    if (needsNavigation && !this.swiper.isBeginning) {
      this.swiperPrevTarget.style.display = 'flex';
    } else {
      this.swiperPrevTarget.style.display = 'none';
    }

    // === 右側按鈕控制 ===
    // 只有在需要導航且不在結束位置時才顯示右箭頭
    if (needsNavigation && !this.swiper.isEnd) {
      this.swiperNextTarget.style.display = 'flex';
    } else {
      this.swiperNextTarget.style.display = 'none';
    }
  }

  //  刷新 Swiper（當 Vue 更新願望清單資料時調用）
  //  延遲執行確保 DOM 更新完成
  refresh() {
    setTimeout(() => {
      this.initializeSwiper();
    }, 100);
  }

  handleResize() {
    // 使用 debounce 避免頻繁重新初始化
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // 檢查當前元素是否可見
      const isVisible = this.element.offsetParent !== null;
      if (isVisible) {
        this.initializeSwiper();
      }
    }, 250);
  }

  disconnect() {
    if (this.swiper) {
      // 完全銷毀 Swiper 實例和相關 DOM 事件
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // 移除視窗大小變化監聽器
    window.removeEventListener('resize', this.handleResize);
    
    // 清理 resize timeout
    clearTimeout(this.resizeTimeout);
  }
}